import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import { isTestMatch, resolvePublicImageUrl } from "../../../helper/helper";
import { colorFor, codeFor } from "../../tournamentPage/matchVm";
import { IcArr, IcDn, IcListL } from "../../tournamentPage/icons";
import type { ICombinedPointsTableResponse, IDivisionsPayload, IDivisionSummary, IGetAllTournaments, IPointsRow, ITournamentsByLeagueResponse } from "../types/home";

type TState = "on" | "done" | "next";
const stateOf = (t: IGetAllTournaments, now = Date.now()): TState => {
  const s = t.startDate ? new Date(t.startDate).getTime() : NaN, e = t.endDate ? new Date(t.endDate).getTime() + 86400000 : NaN;
  if (!Number.isNaN(e) && e < now) return "done";
  if (!Number.isNaN(s) && s > now) return "next";
  return "on";
};

/** Opens on the tournament in progress, otherwise the most recently finished one, otherwise the first listed. */
const pickDefault = (list: IGetAllTournaments[]) => {
  const on = list.filter((t) => stateOf(t) === "on").sort((a, b) => +new Date(b.startDate) - +new Date(a.startDate));
  if (on.length) return on[0];
  const done = list.filter((t) => stateOf(t) === "done").sort((a, b) => +new Date(b.endDate) - +new Date(a.endDate));
  return done[0] ?? list[0];
};

const fmtOv = (balls?: number) => (balls ? `${Math.floor(balls / 6)}${balls % 6 ? `.${balls % 6}` : ""}` : "0");

const Dropdown = ({ id, open, setOpen, label, children, disabled }: { id: string; open: string | null; setOpen: (v: string | null) => void; label: React.ReactNode; children: React.ReactNode; disabled?: boolean }) => (
  <div className="dd">
    <button className="ddb" disabled={disabled} aria-haspopup="listbox" aria-expanded={open === id} onClick={(e) => { e.stopPropagation(); setOpen(open === id ? null : id); }}>{label}<IcDn /></button>
    {open === id && <div className="ddm" role="listbox">{children}</div>}
  </div>
);

/** The points-table endpoint carries no logo, so rows take theirs from the division's team list (by snapshot id, then name). */
type Logos = { byId: Map<string, string>; byName: Map<string, string> };
const logoOf = (r: IPointsRow, logos: Logos) =>
  resolvePublicImageUrl(r.teamImageUrl ?? r.teamLogoUrl ?? (r.teamSnapshotId ? logos.byId.get(r.teamSnapshotId) : undefined) ?? logos.byName.get(r.teamSnapshotName?.trim().toLowerCase()));

const Table = ({ rows, isTest, logos }: { rows: IPointsRow[]; isTest: boolean; logos: Logos }) => (
  <div className="box"><div className="tw"><table className="ptt">
    <thead><tr><th>#</th><th className="l">Team</th><th>M</th><th>W</th><th>L</th><th>T</th><th>NR</th><th className="on">Pts</th>
      <th title={isTest ? "Quotient" : "Net run rate"}>{isTest ? "Quot" : "NRR"}</th>
      {!isTest && <><th title="Runs scored / overs faced">For</th><th title="Runs conceded / overs bowled">Against</th></>}</tr></thead>
    <tbody>
      {rows.map((r, i) => {
        const nrr = r.netRunrate;
        const eq = rows.some((x, j) => j !== i && x.rank === r.rank);
        const logo = logoOf(r, logos);
        return (
          <tr key={r.id ?? i}>
            <td className="ps">{eq ? "=" : ""}{r.rank ?? i + 1}</td>
            <td className="l"><span className="ptm">
              <span className="cr" style={{ background: colorFor(r.teamSnapshotName), overflow: "hidden" }}>
                <span>{codeFor(r.teamSnapshotName)}</span>
                {logo && <img src={logo} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
              </span>
              <b>{r.teamSnapshotName}</b></span></td>
            <td>{r.matches}</td><td>{r.won}</td><td>{r.lost}</td><td>{r.tie}</td><td>{r.noResult}</td>
            <td className="on">{r.points}</td>
            <td className={nrr == null || !r.matches ? "" : nrr >= 0 ? "pos" : "neg"}>{nrr == null || !r.matches ? "–" : `${!isTest && nrr >= 0 ? "+" : ""}${nrr.toFixed(isTest ? 2 : 3)}`}</td>
            {!isTest && <><td>{r.forBalls ? `${r.forRuns}/${fmtOv(r.forBalls)}` : "–"}</td><td>{r.againstBalls ? `${r.againstRuns}/${fmtOv(r.againstBalls)}` : "–"}</td></>}
          </tr>
        );
      })}
      {!rows.length && <tr><td colSpan={isTest ? 9 : 11} className="l"><span className="sm">No teams in this group yet.</span></td></tr>}
    </tbody>
  </table></div></div>
);

const LeaguePointsTable = ({ leagueId }: { leagueId?: string | null }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState<string | null>(null);
  const [tId, setTId] = useState<string | null>(null);
  const [divId, setDivId] = useState<string | null>(null);
  const [grp, setGrp] = useState<string>("all");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("click", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);
  useEffect(() => { setTId(null); setDivId(null); setGrp("all"); }, [leagueId]);

  const tq = useQuery<ITournamentsByLeagueResponse>({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, leagueId],
    enabled: !!leagueId,
    queryFn: async () => (await Api.get<ITournamentsByLeagueResponse>(`${QUERY_KEY.getTournamentsByLeagues}/${leagueId}`)).data,
  });
  const tList = useMemo(() => tq.data?.tournamentResponses ?? [], [tq.data]);
  const tournament = tList.find((t) => t.tournamentId === tId) ?? pickDefault(tList);
  const tournamentId = tournament?.tournamentId ?? null;
  const isTest = isTestMatch(tournament?.matchType);

  const dq = useQuery<IDivisionsPayload>({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    enabled: !!tournamentId,
    queryFn: async () => (await Api.get<IDivisionsPayload>(`${QUERY_KEY.getDivisions}/${tournamentId}`)).data,
  });
  const divisions: IDivisionSummary[] = useMemo(() => {
    const p = dq.data as unknown;
    if (!p) return [];
    if (Array.isArray(p)) return p as IDivisionSummary[];
    const o = p as { divisionResponseList?: IDivisionSummary[]; divisionResponses?: IDivisionSummary[] };
    return o.divisionResponseList ?? o.divisionResponses ?? [];
  }, [dq.data]);
  const division = divisions.find((d) => d.divisionId === divId) ?? divisions[0];
  const groups = useMemo(() => division?.childDivisions ?? [], [division]);
  const logos = useMemo<Logos>(() => {
    const byId = new Map<string, string>(), byName = new Map<string, string>();
    divisions.flatMap((d) => [d, ...(d.childDivisions ?? [])]).forEach((d) => (d.teams ?? []).forEach((t) => {
      if (!t.imageUrl) return;
      if (t.teamSnapshotId) byId.set(t.teamSnapshotId, t.imageUrl);
      if (t.teamName) byName.set(t.teamName.trim().toLowerCase(), t.imageUrl);
    }));
    return { byId, byName };
  }, [divisions]);

  const pq = useQuery<ICombinedPointsTableResponse>({
    queryKey: [QUERY_KEY.getCombinedPointsTableByDivision, division?.divisionId],
    enabled: !!division?.divisionId,
    queryFn: async () => (await Api.get<ICombinedPointsTableResponse>(`${QUERY_KEY.getCombinedPointsTableByDivision}/${division!.divisionId}`)).data,
  });

  const tables = useMemo(() => {
    const data = pq.data ?? {};
    if (!groups.length) return division ? [{ id: division.divisionId, name: division.divisionName, rows: data[division.divisionId] ?? [] }] : [];
    return groups.filter((g) => grp === "all" || g.divisionId === grp).map((g) => ({ id: g.divisionId, name: g.divisionName, rows: data[g.divisionId] ?? [] }));
  }, [pq.data, groups, grp, division]);

  const loading = tq.isLoading || (!!tournamentId && dq.isLoading) || (!!division && pq.isLoading);
  const ts = tournament ? stateOf(tournament) : null;
  const pickT = (id: string) => { setTId(id); setDivId(null); setGrp("all"); setOpen(null); };
  const groupLabel = grp === "all" ? (groups.length > 1 ? "All groups" : groups[0]?.divisionName ?? "All groups") : groups.find((g) => g.divisionId === grp)?.divisionName ?? "Group";
  const rules = tournament ? `Win ${tournament.pointForWin ?? 2} · Tie ${tournament.pointForTie ?? 1} · No result ${tournament.pointForAbandoned ?? 1}. Teams level on points are split by ${isTest ? "quotient" : "net run rate"}.` : "";

  return (
    <section className="lsec" id="secP" aria-labelledby="hP">
      <div className="lsh">
        <h2 id="hP">Points table</h2>
        {tList.length > 0 && (
          <div className="r" ref={ref}>
            <Dropdown id="t" open={open} setOpen={setOpen} label={<span className="tnm">{tournament?.tournamentName}</span>}>
              {tList.map((t) => { const s = stateOf(t); return (
                <button key={t.tournamentId} role="option" className={t.tournamentId === tournamentId ? "on" : ""} onClick={() => pickT(t.tournamentId)}>
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.tournamentName}</span>
                  <span className={`st2 ${s === "on" ? "lv" : ""}`}>{s === "on" ? "IN PROGRESS" : s === "done" ? "FINAL" : "UPCOMING"}</span>
                </button>); })}
            </Dropdown>
            <Dropdown id="d" open={open} setOpen={setOpen} disabled={!divisions.length} label={<span className="tnm">{division?.divisionName ?? "Division"}</span>}>
              {divisions.map((d) => <button key={d.divisionId} role="option" className={d.divisionId === division?.divisionId ? "on" : ""} onClick={() => { setDivId(d.divisionId); setGrp("all"); setOpen(null); }}>{d.divisionName}</button>)}
            </Dropdown>
            <Dropdown id="g" open={open} setOpen={setOpen} disabled={groups.length < 2} label={<span className="tnm">{groupLabel}</span>}>
              <button role="option" className={grp === "all" ? "on" : ""} onClick={() => { setGrp("all"); setOpen(null); }}>All groups</button>
              {groups.map((g) => <button key={g.divisionId} role="option" className={grp === g.divisionId ? "on" : ""} onClick={() => { setGrp(g.divisionId); setOpen(null); }}>{g.divisionName}</button>)}
            </Dropdown>
          </div>
        )}
      </div>
      <div className="lh-pt">
        {loading ? (
          <div className="box"><div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>{[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="sk" style={{ height: 22 }} />)}</div></div>
        ) : !tList.length || !tables.length ? (
          <div className="empty" style={{ marginTop: 0 }}><span className="ic"><IcListL /></span><h3>No points table yet</h3><p>{tList.length ? "This tournament's table appears here once teams are added to its divisions." : "The table for the league's first tournament appears here once teams are added."}</p></div>
        ) : (
          <>
            {ts === "done" && <div className="notice info"><span><b>Final standings.</b> {tournament?.tournamentName} has finished.</span></div>}
            {tables.map((t) => (
              <React.Fragment key={t.id}>
                {tables.length > 1 && <div className="divh"><h3>{t.name}</h3><span>{t.rows.length} teams</span></div>}
                <Table rows={t.rows} isTest={isTest} logos={logos} />
              </React.Fragment>
            ))}
            <div className="ptf"><span>{rules}</span>
              {tournamentId && <a className="alink" href={`/my-leagues/${leagueId}/tournaments/${tournamentId}`} onClick={(e) => { e.preventDefault(); nav(`/my-leagues/${leagueId}/tournaments/${tournamentId}`); }}>Open {tournament?.tournamentName}<IcArr /></a>}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default LeaguePointsTable;
